import { z } from 'zod';
import { supabase } from '@/lib/supabase/client';
import type { Tables } from '@/lib/supabase/types';

export type Document = Tables<'documents'>;

export const documentTitleSchema = z
  .string()
  .min(1, 'Title is required')
  .max(200, 'Title is too long');

export async function getDocumentsByPublication(publicationId: string): Promise<Document[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('publication_id', publicationId)
    .is('deleted_at', null)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function getDocument(id: string): Promise<Document> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function createDocument(
  publicationId: string,
  title: string,
): Promise<{ documentId: string; versionId: string }> {
  documentTitleSchema.parse(title);
  const { data, error } = await supabase.rpc('create_document_with_draft', {
    p_publication_id: publicationId,
    p_title: title,
  });
  if (error) throw error;
  const row = data as { document_id: string; version_id: string };
  return { documentId: row.document_id, versionId: row.version_id };
}

export async function updateDocumentTitle(id: string, title: string): Promise<Document> {
  documentTitleSchema.parse(title);
  const { data, error } = await supabase
    .from('documents')
    .update({ title })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function moveDocumentToPublication(id: string, publicationId: string): Promise<Document> {
  const { data, error } = await supabase
    .from('documents')
    .update({ publication_id: publicationId })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// 30-minute cooldown per document per device — prevents a single reader from
// inflating the count on every refresh/reopen.
const VIEW_COOLDOWN_MS = 30 * 60 * 1000;

export async function trackDocumentView(documentId: string): Promise<void> {
  const key = `oipub-viewed-${documentId}`;
  const last = parseInt(localStorage.getItem(key) ?? '0', 10);
  if (Date.now() - last < VIEW_COOLDOWN_MS) return;
  const { error } = await supabase.rpc('increment_view_count', { p_document_id: documentId });
  if (error) throw error;
  localStorage.setItem(key, String(Date.now()));
}

export async function archiveDocument(id: string): Promise<void> {
  const { error } = await supabase
    .from('documents')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface PublishedDocumentSummary {
  id: string;
  title: string;
  publishedAt: string;
  viewCount: number;
  coverImageUrl: string | null;
  publication: {
    id: string;
    title: string;
    kind: string;
    authorName: string;
    authorHandle: string;
  };
}

type DocWithPub = {
  id: string;
  title: string;
  author: string | null;
  published_at: string | null;
  view_count: number;
  cover_image_url: string | null;
  publications: {
    id: string;
    title: string;
    kind: string;
    visibility: string;
    profiles: { handle: string | null; display_name: string | null } | null;
  };
};

export async function getPublishedDocuments(limit = 48): Promise<PublishedDocumentSummary[]> {
  const { data, error } = await supabase
    .from('documents')
    .select(`
      id,
      title,
      author,
      published_at,
      view_count,
      cover_image_url,
      publications!inner(
        id,
        title,
        kind,
        visibility,
        profiles!author_id(handle, display_name)
      )
    `)
    .is('deleted_at', null)
    .not('current_version_id', 'is', null)
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (error) throw error;

  return ((data ?? []) as unknown as DocWithPub[])
    .filter(d => d.publications.visibility === 'public' || d.publications.visibility === 'unlisted')
    .map(d => ({
      id: d.id,
      title: d.title,
      publishedAt: d.published_at ?? '',
      viewCount: d.view_count ?? 0,
      coverImageUrl: d.cover_image_url ?? null,
      publication: {
        id: d.publications.id,
        title: d.publications.title,
        kind: d.publications.kind,
        authorName: d.author ?? d.publications.profiles?.display_name ?? (d.publications.profiles?.handle ? `@${d.publications.profiles.handle}` : 'Unknown'),
        authorHandle: d.publications.profiles?.handle ?? '',
      },
    }));
}
